import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { ModuleFlag } from "@/lib/modules/types";

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface ModuleContextValue {
  readonly modules: readonly ModuleFlag[];
  readonly isLoading: boolean;
  isModuleEnabled(moduleKey: string, region?: string): boolean;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ModuleContext = createContext<ModuleContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ModuleProviderProps {
  readonly modules: readonly ModuleFlag[];
  readonly isLoading: boolean;
  readonly children: ReactNode;
}

export function ModuleProvider({
  modules,
  isLoading,
  children,
}: ModuleProviderProps) {
  const value = useMemo<ModuleContextValue>(
    () => ({
      modules,
      isLoading,
      isModuleEnabled(moduleKey: string, region?: string): boolean {
        const module = modules.find((m) => m.moduleKey === moduleKey);

        if (!module || !module.isEnabled) {
          return false;
        }

        if (
          region !== undefined &&
          module.regions.length > 0 &&
          !module.regions.includes(region)
        ) {
          return false;
        }

        return true;
      },
    }),
    [modules, isLoading],
  );

  return (
    <ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the module context value.
 *
 * Falls back to a safe empty default when no `ModuleProvider` ancestor exists
 * — this prevents a React render crash on pages where the provider wrapper is
 * missing or hasn't mounted yet (e.g. stale production builds, Suspense
 * boundaries, or race conditions during lazy-load).
 */
const EMPTY_CONTEXT: ModuleContextValue = {
  modules: [],
  isLoading: true,
  isModuleEnabled: () => false,
};

export function useModuleContext(): ModuleContextValue {
  const ctx = useContext(ModuleContext);
  return ctx ?? EMPTY_CONTEXT;
}
